import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, GripVertical, Loader2, Trash2, Users } from 'lucide-react';
import { createCreneau, updateCreneau, deleteCreneau, type CreneauPayload } from '../services/api';
import Dialogue from './Dialogue';

/** Plage horaire couverte par la grille, bornes incluses côté début. */
const HEURE_DEBUT = 8;
const HEURE_FIN = 24;

const jourLong = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

const couleurStatut: Record<string, string> = {
  confirme: 'bg-green-100 text-green-800 border-green-200',
  propose: 'bg-amber-100 text-amber-800 border-amber-200',
  refuse: 'bg-red-100 text-red-800 border-red-200',
  realise: 'bg-blue-100 text-blue-800 border-blue-200',
};

interface Creneau {
  id: number;
  mission: string;
  missionLabel?: string;
  statutConfirmation: string;
  debut: string;
  fin: string;
  nbHeures?: number | null;
  commentaire?: string | null;
  benevole?: { id: number; fullName: string } | null;
  eventParticipant?: { id: number } | null;
}

interface Benevole {
  id: number;
  fullName: string;
}

interface Props {
  evenementId: number;
  creneaux: Creneau[];
  /** Missions et bénévoles renvoyés par /referentiel. */
  referentiel: any;
  /** Dates de l'événement : elles déterminent les journées affichées. */
  dateDebut?: string | null;
  dateFin?: string | null;
  onSucces: (message: string) => void;
  onEchec: (error: Error) => void;
}

/** Clé locale d'un instant, sans décalage de fuseau. */
function cleHeure(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}T${String(date.getHours()).padStart(2, '0')}`;
}

/** Format attendu par l'API : datetime local sans fuseau. */
function pourApi(jour: Date, heure: number): string {
  const d = new Date(jour);
  d.setHours(heure, 0, 0, 0);

  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:00`;
}

/**
 * Emploi du temps d'un événement : missions en lignes, heures en colonnes.
 *
 * On dépose un bénévole depuis la palette dans une case pour lui affecter une
 * heure sur une mission ; on déplace une pastille d'une case à l'autre pour la
 * décaler. Le formulaire détaillé reste accessible pour ce que la grille ne
 * sait pas exprimer — un créneau à cheval sur deux heures, un commentaire.
 */
export default function GrillePlanning({
  evenementId,
  creneaux,
  referentiel,
  dateDebut,
  dateFin,
  onSucces,
  onEchec,
}: Props) {
  const queryClient = useQueryClient();
  const [mission, setMission] = useState<string | null>(null);
  const [survolee, setSurvolee] = useState<string | null>(null);
  // Les journées repliées, par leur date. Rien n'est replié au départ : c'est
  // la première ouverture qui doit montrer le planning entier.
  const [repliees, setRepliees] = useState<string[]>([]);

  const missions: Array<[string, string]> = Object.entries(referentiel?.missions ?? {}) as Array<
    [string, string]
  >;
  const benevoles: Benevole[] = referentiel?.benevoles ?? [];

  const heures = useMemo(
    () => Array.from({ length: HEURE_FIN - HEURE_DEBUT }, (_, i) => HEURE_DEBUT + i),
    [],
  );

  // Journées couvertes : celles de l'événement, encadrées par la veille et le
  // lendemain, et complétées par celles où un créneau existe déjà — un créneau
  // posé hors bornes doit rester visible.
  //
  // Une fête ne commence pas le jour de la fête : il y a le montage la veille
  // et le rangement le lendemain, et ces heures-là doivent se planifier comme
  // les autres.
  const jours = useMemo(() => {
    const cles = new Set<string>();
    const ajouter = (d: Date) => cles.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);

    if (dateDebut) {
      const debut = new Date(dateDebut);
      const fin = dateFin ? new Date(dateFin) : debut;

      const curseur = new Date(debut);
      curseur.setHours(0, 0, 0, 0);
      curseur.setDate(curseur.getDate() - 1); // J-1 : préparatifs

      const dernier = new Date(fin);
      dernier.setHours(0, 0, 0, 0);
      dernier.setDate(dernier.getDate() + 1); // J+1 : rangement

      while (curseur <= dernier) {
        ajouter(curseur);
        curseur.setDate(curseur.getDate() + 1);
      }
    }

    creneaux.forEach((c) => c.debut && ajouter(new Date(c.debut)));

    return Array.from(cles)
      .map((cle) => {
        const [a, m, j] = cle.split('-').map(Number);
        return new Date(a, m, j);
      })
      .sort((a, b) => a.getTime() - b.getTime());
  }, [dateDebut, dateFin, creneaux]);

  /** Rôle d'une journée par rapport à l'événement, affiché en tête de grille. */
  const roleDuJour = (jour: Date): string | null => {
    if (!dateDebut) return null;

    const memeJour = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

    const veille = new Date(dateDebut);
    veille.setDate(veille.getDate() - 1);

    const lendemain = new Date(dateFin ?? dateDebut);
    lendemain.setDate(lendemain.getDate() + 1);

    if (memeJour(jour, veille)) return 'Préparatifs';
    if (memeJour(jour, lendemain)) return 'Rangement';

    return null;
  };

  // Index (mission, heure) → créneaux, pour ne pas balayer la liste par case.
  const parCase = useMemo(() => {
    const index = new Map<string, Creneau[]>();

    creneaux.forEach((c) => {
      if (!c.debut) return;
      const cle = `${c.mission}|${cleHeure(new Date(c.debut))}`;
      index.set(cle, [...(index.get(cle) ?? []), c]);
    });

    return index;
  }, [creneaux]);

  const rafraichir = (message: string) => {
    queryClient.invalidateQueries({ queryKey: ['evenement-planning', evenementId] });
    onSucces(message);
  };

  /**
   * Un conflit d'horaire opposé par le serveur, et le geste qui l'a provoqué.
   *
   * Le refus n'est pas une fin : le bureau sait parfois ce qu'il fait — deux
   * postes voisins tenus par la même personne. On garde donc de quoi rejouer
   * l'affectation telle quelle, en passant outre. Ce qui compte est qu'on ne
   * passe pas outre sans avoir lu contre quoi.
   */
  const [conflit, setConflit] = useState<{ message: string; forcer: () => void } | null>(null);

  /** Un échec qui nomme un conflit s'arbitre ; les autres se signalent. */
  const arbitrer = (erreur: Error, forcer: () => void) => {
    const donnees = (erreur as Error & { donnees?: { conflit?: boolean } }).donnees;
    if (donnees?.conflit) {
      setConflit({ message: erreur.message, forcer });
      return;
    }
    onEchec(erreur);
  };

  const affecter = useMutation({
    mutationFn: (payload: CreneauPayload) => createCreneau(evenementId, payload),
    onSuccess: () => {
      setConflit(null);
      rafraichir('Créneau affecté.');
    },
    onError: (erreur, payload) =>
      arbitrer(erreur, () => affecter.mutate({ ...payload, forcer: true })),
  });

  const deplacer = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CreneauPayload }) =>
      updateCreneau(evenementId, id, payload),
    onSuccess: () => {
      setConflit(null);
      rafraichir('Créneau déplacé.');
    },
    onError: (erreur, variables) =>
      arbitrer(erreur, () =>
        deplacer.mutate({ id: variables.id, payload: { ...variables.payload, forcer: true } })
      ),
  });

  const retirer = useMutation({
    mutationFn: (id: number) => deleteCreneau(evenementId, id),
    onSuccess: () => rafraichir('Créneau retiré.'),
    onError: onEchec,
  });

  const enCours = affecter.isPending || deplacer.isPending || retirer.isPending;

  function deposer(event: React.DragEvent, missionCode: string, jour: Date, heure: number) {
    event.preventDefault();
    setSurvolee(null);

    const brut = event.dataTransfer.getData('application/json');
    if (!brut) return;

    const charge = JSON.parse(brut) as
      | { type: 'benevole'; benevoleId: number }
      | {
          type: 'creneau';
          creneauId: number;
          benevoleId: number;
          statut: string;
          eventParticipantId: number | null;
          commentaire: string | null;
        };

    const debut = pourApi(jour, heure);
    const fin = pourApi(jour, Math.min(heure + 1, HEURE_FIN));

    if (charge.type === 'creneau') {
      deplacer.mutate({
        id: charge.creneauId,
        payload: {
          benevoleId: charge.benevoleId,
          eventParticipantId: charge.eventParticipantId,
          mission: missionCode,
          statutConfirmation: charge.statut,
          debut,
          fin,
          commentaire: charge.commentaire,
        },
      });
      return;
    }

    affecter.mutate({
      benevoleId: charge.benevoleId,
      mission: missionCode,
      statutConfirmation: 'propose',
      debut,
      fin,
    });
  }

  if (missions.length === 0 || jours.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        La grille a besoin d'une date d'événement et d'au moins une mission pour s'afficher.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* Le conflit se règle là où on l'a provoqué, sans quitter la grille :
          on lit contre quoi l'on bute, puis on tranche. */}
      {conflit && (
        <Dialogue
          titre="Ce créneau en chevauche un autre"
          message={conflit.message}
          precision="Personne ne peut être à deux endroits à la fois — sauf si vous savez pourquoi : une relève de dix minutes, ou deux postes voisins tenus ensemble."
          libelleConfirmer="Affecter quand même"
          onConfirmer={() => conflit.forcer()}
          onFermer={() => setConflit(null)}
        />
      )}

      {/* La palette reste en haut de page pendant tout le glisser-deposer.

          La grille fait plusieurs ecrans de haut : des qu'on descend vers un
          creneau de fin d'apres-midi, les pastilles a poser sortaient du champ.
          Il fallait remonter chercher la personne, redescendre, et recommencer a
          chaque affectation.

          Ce bloc collant n'etait pas possible avant : la mise en page enfermait
          le contenu dans un conteneur de defilement qui ne defilait pas, et qui
          servait pourtant de reference aux blocs collants — ils repartaient donc
          avec la page. Ce conteneur a ete retire. */}
      <div className="card lg:p-5 space-y-2 sticky top-0 z-20 shadow-sm">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Users className="w-4 h-4 text-primary-600" />
          Membres et bénévoles
          {enCours && <Loader2 className="w-4 h-4 animate-spin text-primary-600" />}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Glissez une personne dans une case pour l'affecter à une mission sur ce créneau d'une
          heure. Une pastille déjà posée se déplace de la même façon.
        </p>

        {benevoles.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Aucun bénévole enregistré. Un membre du bureau doit d'abord avoir une fiche bénévole
            pour être planifié.
          </p>
        ) : (
            <ul className="flex flex-wrap gap-2 pt-1 max-h-32 overflow-y-auto">
            {benevoles.map((b) => (
              <li
                key={b.id}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'copy';
                  e.dataTransfer.setData(
                    'application/json',
                    JSON.stringify({ type: 'benevole', benevoleId: b.id }),
                  );
                }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-primary-50 text-primary-800 text-sm border border-primary-200 cursor-grab active:cursor-grabbing select-none"
              >
                <GripVertical className="w-3.5 h-3.5 text-primary-400" />
                {b.fullName}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Filtre de mission : au-delà de quatre lignes, la grille devient large. */}
      {missions.length > 4 && (
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => setMission(null)}
            className={`text-xs px-2.5 py-1 rounded-full ${
              mission === null ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 dark:text-gray-400 dark:bg-gray-700'
            }`}
          >
            Toutes les missions
          </button>
          {missions.map(([code, libelle]) => (
            <button
              key={code}
              type="button"
              onClick={() => setMission(code)}
              className={`text-xs px-2.5 py-1 rounded-full ${
                mission === code ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 dark:text-gray-400 dark:bg-gray-700'
              }`}
            >
              {libelle}
            </button>
          ))}
        </div>
      )}

      {jours.map((jour) => {
        const cle = jour.toISOString();
        const repliee = repliees.includes(cle);
        const poseesCeJour = creneaux.filter(
          (c) => c.debut && new Date(c.debut).toDateString() === jour.toDateString()
        ).length;

        return (
        <section key={cle} className="space-y-2">
          {/* Une flèche pour replier la journée. Une fête de trois jours
              empile trois grilles de seize heures : pour affecter quelqu'un le
              dimanche, il fallait dépasser samedi entier, et la palette des
              personnes restait tout en haut, hors de portée. Replier ce qui
              est fait ramène la grille suivante sous la main. */}
          <h3 className="font-semibold capitalize flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() =>
                setRepliees((v) => (v.includes(cle) ? v.filter((x) => x !== cle) : [...v, cle]))
              }
              aria-expanded={!repliee}
              title={repliee ? 'Déplier cette journée' : 'Replier cette journée'}
              className="inline-flex items-center justify-center w-6 h-6 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <ChevronRight
                className={`w-4 h-4 transition-transform ${repliee ? '' : 'rotate-90'}`}
              />
            </button>
            {jourLong.format(jour)}
            {roleDuJour(jour) && (
              <span className="text-xs font-normal normal-case px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                {roleDuJour(jour)}
              </span>
            )}
            {/* Repliée, la journée doit dire ce qu'elle cache : sans ce
                nombre, on la déplie pour vérifier qu'on n'a rien oublié. */}
            {repliee && (
              <span className="text-xs font-normal normal-case text-gray-500 dark:text-gray-400">
                {poseesCeJour === 0
                  ? 'aucun créneau'
                  : `${poseesCeJour} créneau${poseesCeJour > 1 ? 'x' : ''}`}
              </span>
            )}
          </h3>

          <div className={`card p-0 overflow-x-auto ${repliee ? 'hidden' : ''}`}>
            <table className="text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800">
                  <th className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300 min-w-[9rem] border-r border-gray-200 dark:border-gray-700">
                    Mission
                  </th>
                  {heures.map((h) => (
                    <th
                      key={h}
                      className="px-1 py-2 font-medium text-gray-500 text-xs min-w-[5.5rem] border-r border-gray-100 dark:border-gray-800 dark:text-gray-400"
                    >
                      {String(h).padStart(2, '0')}h
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {missions
                  .filter(([code]) => mission === null || code === mission)
                  .map(([code, libelle]) => (
                    <tr key={code} className="border-t border-gray-100 dark:border-gray-800">
                      <th className="sticky left-0 z-10 bg-white dark:bg-gray-900 px-3 py-2 text-left font-medium text-gray-800 dark:text-gray-100 border-r border-gray-200 dark:border-gray-700">
                        {libelle}
                      </th>

                      {heures.map((h) => {
                        const reference = new Date(jour);
                        reference.setHours(h, 0, 0, 0);
                        const cle = `${code}|${cleHeure(reference)}`;
                        const contenu = parCase.get(cle) ?? [];
                        const active = survolee === cle;

                        return (
                          <td
                            key={h}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'copy';
                              setSurvolee(cle);
                            }}
                            onDragLeave={() => setSurvolee((s) => (s === cle ? null : s))}
                            onDrop={(e) => deposer(e, code, jour, h)}
                            className={`align-top p-1 border-r border-gray-100 dark:border-gray-800 transition-colors ${
                              active ? 'bg-primary-50 dark:bg-primary-900/30' : ''
                            }`}
                          >
                            <div className="min-h-[2.75rem] space-y-1">
                              {contenu.map((c) => (
                                <div
                                  key={c.id}
                                  draggable
                                  onDragStart={(e) => {
                                    e.dataTransfer.effectAllowed = 'move';
                                    e.dataTransfer.setData(
                                      'application/json',
                                      JSON.stringify({
                                        type: 'creneau',
                                        creneauId: c.id,
                                        benevoleId: c.benevole?.id,
                                        statut: c.statutConfirmation,
                                        eventParticipantId: c.eventParticipant?.id ?? null,
                                        commentaire: c.commentaire ?? null,
                                      }),
                                    );
                                  }}
                                  className={`group text-[11px] leading-tight px-1.5 py-1 rounded-lg border cursor-grab active:cursor-grabbing flex items-start gap-1 ${
                                    couleurStatut[c.statutConfirmation] ?? 'bg-gray-100 border-gray-200'
                                  }`}
                                  title={`${c.benevole?.fullName ?? 'Bénévole supprimé'} — ${
                                    c.missionLabel ?? c.mission
                                  } (${c.statutConfirmation})`}
                                >
                                  <span className="flex-1 truncate">
                                    {c.benevole?.fullName ?? '—'}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => retirer.mutate(c.id)}
                                    disabled={enCours}
                                    title="Retirer ce créneau"
                                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-current hover:text-red-700 shrink-0"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
        );
      })}

      <p className="text-xs text-gray-400">
        Les couleurs suivent la confirmation : ambre proposé, vert confirmé, rouge refusé, bleu
        réalisé.
      </p>
    </div>
  );
}
